import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Users, Briefcase, Building2, FileText, RefreshCw, ChevronDown } from 'lucide-react';

const API   = process.env.REACT_APP_BACKEND_URL + '/api/jb/admin';
const TOKEN = () => localStorage.getItem('auth_token') || '';

const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${TOKEN()}` });

async function apiFetch(path, opts = {}) {
  const res = await fetch(API + path, { headers: hdrs(), ...opts });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

// ── Stat card ───────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color = '#1B6B72' }) {
  return (
    <div style={{ background:'#fff', borderRadius:12, padding:'20px 22px', boxShadow:'0 2px 8px rgba(13,33,55,.07)', display:'flex', alignItems:'flex-start', gap:14 }}>
      <div style={{ width:44, height:44, borderRadius:10, background:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize:26, fontWeight:900, color:'#0D2137', fontFamily:'Inter,sans-serif', lineHeight:1 }}>{value ?? '—'}</div>
        <div style={{ fontSize:12, fontWeight:700, color:'#0D2137', marginTop:4 }}>{label}</div>
        {sub && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  active:           { bg:'#f0fdf4', color:'#166534' },
  pending_approval: { bg:'#fef9c3', color:'#854d0e' },
  pending_payment:  { bg:'#fff7ed', color:'#9a3412' },
  rejected:         { bg:'#fef2f2', color:'#991b1b' },
  expired:          { bg:'#f8fafc', color:'#64748b' },
  draft:            { bg:'#f8fafc', color:'#64748b' },
};
function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg:'#f8fafc', color:'#64748b' };
  return <span style={{ ...s, padding:'3px 10px', borderRadius:999, fontSize:11, fontWeight:700 }}>{status?.replace(/_/g,' ')}</span>;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}

// ── Job Queue Tab ─────────────────────────────────────────────────────────────

function JobQueueTab() {
  const [jobs, setJobs]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/jobs?status=pending_approval')
      .then(d => setJobs(d.jobs || []))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function approve(jobId) {
    try {
      await apiFetch(`/jobs/${jobId}/approve`, { method:'POST' });
      toast.success('Job approved and employer notified.');
      load();
    } catch(e) { toast.error(e.message); }
  }

  async function reject(jobId) {
    if (!window.confirm('Reject this job posting?')) return;
    try {
      await apiFetch(`/jobs/${jobId}/reject`, { method:'POST' });
      toast.success('Job rejected.');
      load();
    } catch(e) { toast.error(e.message); }
  }

  if (loading) return <div style={{ textAlign:'center', padding:60, color:'#5a6373' }}>Loading queue…</div>;

  return (
    <div data-testid="job-queue-tab">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:'#0D2137' }}>Pending Approval ({jobs.length})</h3>
        <button onClick={load} style={{ background:'none', border:'none', cursor:'pointer', color:'#1B6B72', display:'flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600, fontFamily:'Inter,sans-serif' }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {jobs.length === 0 && (
        <div style={{ background:'#f8fafc', borderRadius:10, padding:'32px 20px', textAlign:'center', color:'#94a3b8', fontSize:13 }}>
          No jobs pending approval.
        </div>
      )}

      {jobs.map(job => (
        <div key={job.id} data-testid={`queue-job-${job.id}`} style={{ background:'#fff', borderRadius:10, padding:'16px 18px', marginBottom:10, boxShadow:'0 2px 8px rgba(13,33,55,.07)', display:'flex', alignItems:'flex-start', gap:14 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
              <span style={{ fontSize:14, fontWeight:700, color:'#0D2137' }}>{job.title}</span>
              <StatusBadge status={job.status} />
              <span style={{ fontSize:11, color:'#94a3b8' }}>Plan: {job.plan_tier}</span>
            </div>
            <div style={{ fontSize:12, color:'#5a6373' }}>
              {job.organization} · {job.location} · Submitted {fmtDate(job.created_at)}
            </div>
            {job.description && (
              <div style={{ fontSize:12, color:'#64748b', marginTop:6, overflow:'hidden', textOverflow:'ellipsis', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                {job.description}
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:8, flexShrink:0, alignItems:'center' }}>
            <button onClick={() => approve(job.id)} data-testid={`approve-${job.id}`} style={{
              display:'flex', alignItems:'center', gap:5, padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
              background:'#f0fdf4', border:'1px solid #86efac', color:'#166534', fontFamily:'Inter,sans-serif',
            }}>
              <CheckCircle size={13} /> Approve
            </button>
            <button onClick={() => reject(job.id)} data-testid={`reject-${job.id}`} style={{
              display:'flex', alignItems:'center', gap:5, padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
              background:'#fef2f2', border:'1px solid #fca5a5', color:'#991b1b', fontFamily:'Inter,sans-serif',
            }}>
              <XCircle size={13} /> Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── All Jobs Tab ──────────────────────────────────────────────────────────────

function AllJobsTab() {
  const [jobs, setJobs]     = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = filter ? `?status=${filter}` : '';
    apiFetch(`/jobs${q}`)
      .then(d => setJobs(d.jobs || []))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [filter]);

  const statuses = ['', 'active', 'pending_approval', 'pending_payment', 'rejected', 'expired'];

  return (
    <div data-testid="all-jobs-tab">
      <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
        {statuses.map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            padding:'5px 12px', borderRadius:999, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'Inter,sans-serif',
            background: filter === s ? '#0D2137' : '#f8fafc',
            color: filter === s ? '#F5F0E8' : '#5a6373',
            border: filter === s ? '1px solid #0D2137' : '1px solid #ddd8cc',
          }}>{s || 'All'}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#5a6373' }}>Loading…</div>
      ) : (
        <div>
          <div style={{ fontSize:12, color:'#94a3b8', marginBottom:10 }}>{jobs.length} job{jobs.length !== 1 ? 's' : ''}</div>
          {jobs.map(job => (
            <div key={job.id} data-testid={`job-row-${job.id}`} style={{ background:'#fff', borderRadius:10, padding:'12px 16px', marginBottom:8, boxShadow:'0 1px 4px rgba(13,33,55,.06)', display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#0D2137', marginBottom:2 }}>{job.title}</div>
                <div style={{ fontSize:11, color:'#5a6373' }}>{job.organization} · {job.location} · {fmtDate(job.created_at)}</div>
              </div>
              <StatusBadge status={job.status} />
              <span style={{ fontSize:11, color:'#94a3b8', flexShrink:0 }}>{job.plan_tier}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Employers Tab ─────────────────────────────────────────────────────────────

function EmployersTab() {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    apiFetch('/employers')
      .then(d => setEmployers(d.employers || []))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="employers-tab">
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#5a6373' }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize:12, color:'#94a3b8', marginBottom:12 }}>{employers.length} employer{employers.length !== 1 ? 's' : ''}</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2ddd4' }}>
                  {['Company', 'Contact', 'Email', 'Joined', 'Logo'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#0D2137', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employers.map(e => (
                  <tr key={e.id} data-testid={`employer-row-${e.id}`} style={{ borderBottom:'1px solid #f0ece6' }}>
                    <td style={{ padding:'10px 12px', fontWeight:600, color:'#0D2137' }}>{e.company_name}</td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>{e.contact_name || '—'}</td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>{e.contact_email}</td>
                    <td style={{ padding:'10px 12px', color:'#94a3b8', whiteSpace:'nowrap' }}>{fmtDate(e.created_at)}</td>
                    <td style={{ padding:'10px 12px' }}>
                      {e.company_logo_url ? (
                        <img src={e.company_logo_url} alt="" style={{ width:28, height:28, borderRadius:4, objectFit:'contain', border:'1px solid #ddd8cc' }} />
                      ) : <span style={{ color:'#94a3b8' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Seekers Tab ───────────────────────────────────────────────────────────────

function SeekersTab() {
  const [seekers, setSeekers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/seekers')
      .then(d => setSeekers(d.seekers || []))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="seekers-tab">
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#5a6373' }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize:12, color:'#94a3b8', marginBottom:12 }}>{seekers.length} seeker{seekers.length !== 1 ? 's' : ''}</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2ddd4' }}>
                  {['Name', 'Email', 'Headline', 'Experience', 'First Briefcase', 'Joined'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#0D2137', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {seekers.map(s => (
                  <tr key={s.id} data-testid={`seeker-row-${s.id}`} style={{ borderBottom:'1px solid #f0ece6' }}>
                    <td style={{ padding:'10px 12px', fontWeight:600, color:'#0D2137' }}>{s.first_name} {s.last_name}</td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>{s.email}</td>
                    <td style={{ padding:'10px 12px', color:'#374151', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.headline || '—'}</td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>{s.experience_level || '—'}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center' }}>
                      {s.is_first_briefcase
                        ? <span style={{ fontSize:13, color:'#166534' }}>✓</span>
                        : <span style={{ fontSize:11, color:'#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ padding:'10px 12px', color:'#94a3b8', whiteSpace:'nowrap' }}>{fmtDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Applications Tab ──────────────────────────────────────────────────────────

const APP_COLORS = {
  applied:   { bg:'#f0f9ff', color:'#0369a1' },
  reviewed:  { bg:'#f0fdf4', color:'#166534' },
  interview: { bg:'#faf5ff', color:'#6d28d9' },
  offered:   { bg:'#f0fdf4', color:'#166534' },
  rejected:  { bg:'#fef2f2', color:'#991b1b' },
  withdrawn: { bg:'#f8fafc', color:'#64748b' },
};
function AppBadge({ status }) {
  const s = APP_COLORS[status] || { bg:'#f8fafc', color:'#64748b' };
  return <span style={{ ...s, padding:'3px 10px', borderRadius:999, fontSize:11, fontWeight:700 }}>{status}</span>;
}

function ApplicationsTab() {
  const [apps, setApps]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/applications')
      .then(d => setApps(d.applications || []))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="applications-tab">
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#5a6373' }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize:12, color:'#94a3b8', marginBottom:12 }}>{apps.length} application{apps.length !== 1 ? 's' : ''}</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2ddd4' }}>
                  {['Job ID', 'Seeker', 'Cover Note', 'Status', 'Applied'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#0D2137', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apps.map(a => (
                  <tr key={a.id} data-testid={`app-row-${a.id}`} style={{ borderBottom:'1px solid #f0ece6' }}>
                    <td style={{ padding:'10px 12px', color:'#94a3b8', fontFamily:'monospace', fontSize:11 }}>{a.job_id?.slice(-8)}</td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>{a.seeker_name || a.seeker_id?.slice(-8) || '—'}</td>
                    <td style={{ padding:'10px 12px', color:'#374151', maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.cover_note || '—'}</td>
                    <td style={{ padding:'10px 12px' }}><AppBadge status={a.status} /></td>
                    <td style={{ padding:'10px 12px', color:'#94a3b8', whiteSpace:'nowrap' }}>{fmtDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const TABS = [
  { id:'queue',    label:'Approval Queue', Icon: CheckCircle },
  { id:'all-jobs', label:'All Jobs',       Icon: Briefcase },
  { id:'employers',label:'Employers',      Icon: Building2 },
  { id:'seekers',  label:'Seekers',        Icon: Users },
  { id:'apps',     label:'Applications',   Icon: FileText },
];

export default function AdminJobBoardPage() {
  const [tab, setTab]       = useState('queue');
  const [stats, setStats]   = useState(null);

  useEffect(() => {
    apiFetch('/stats').then(setStats).catch(() => {});
  }, []);

  return (
    <AdminLayout title="Job Board" breadcrumb="Job Board Admin">
      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:14, marginBottom:24 }} data-testid="jb-admin-stats">
        <StatCard icon={Briefcase}  label="Total Jobs"         value={stats?.jobs?.total}            sub={`${stats?.jobs?.active ?? '—'} active · ${stats?.jobs?.pending_approval ?? '—'} pending`} color="#0D2137" />
        <StatCard icon={Building2}  label="Employers"          value={stats?.employers?.total}       color="#1B6B72" />
        <StatCard icon={Users}      label="Seekers"            value={stats?.seekers?.total}         color="#C9A84C" />
        <StatCard icon={FileText}   label="Applications"       value={stats?.applications?.total}    color="#6d28d9" />
      </div>

      {/* Tabs */}
      <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 2px 8px rgba(13,33,55,.07)', overflow:'hidden' }}>
        <div style={{ display:'flex', borderBottom:'1px solid #e2ddd4', overflowX:'auto' }} data-testid="jb-admin-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              style={{
                padding:'14px 18px', fontSize:13, fontWeight:600, cursor:'pointer', border:'none', background:'none',
                display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap', fontFamily:'Inter,sans-serif',
                color: tab === id ? '#1B6B72' : '#5a6373',
                borderBottom: tab === id ? '2px solid #1B6B72' : '2px solid transparent',
                marginBottom:-1,
              }}
            >
              <Icon size={14} />{label}
            </button>
          ))}
        </div>
        <div style={{ padding:'20px 22px' }}>
          {tab === 'queue'     && <JobQueueTab />}
          {tab === 'all-jobs'  && <AllJobsTab />}
          {tab === 'employers' && <EmployersTab />}
          {tab === 'seekers'   && <SeekersTab />}
          {tab === 'apps'      && <ApplicationsTab />}
        </div>
      </div>
    </AdminLayout>
  );
}
